import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { StatusBar } from "./StatusBar";
import { DeviceCheck } from "./DeviceCheck";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { getUserSettings } from "@/lib/userSettings";
import { isCaregiverRoute } from "@/lib/caregiverRoutes";
import { useEyeControlInput } from "@/hooks/useEyeControlInput";

interface MainLayoutProps {
  children: React.ReactNode;
  onBack?: () => void;
  hideSidebar?: boolean;
  onSidebarActionsReady?: (actions: {
    onHelp?: () => void;
    onRest?: () => void;
    onClock?: () => void;
    onVolume?: () => void;
  }) => void;
}

export function MainLayout({
  children,
  onBack,
  hideSidebar,
  onSidebarActionsReady
}: MainLayoutProps) {
  const { dir, isRTL } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Reactive font size update (same-tab + cross-tab)
  const [currentFontSize, setCurrentFontSize] = useState<"small" | "medium" | "large">(
    () => getUserSettings().fontSize || "medium"
  );
  useEffect(() => {
    const updateFontSize = () => {
      setCurrentFontSize(getUserSettings().fontSize || "medium");
    };

    updateFontSize();
    window.addEventListener("storage", updateFontSize);
    window.addEventListener("userSettingsChanged", updateFontSize as EventListener);

    return () => {
      window.removeEventListener("storage", updateFontSize);
      window.removeEventListener("userSettingsChanged", updateFontSize as EventListener);
    };
  }, []);

  // Activate EyeControl input globally (not on caregiver pages)
  useEyeControlInput({ enabled: !isCaregiverRoute(location.pathname) });

  // Check if current page is a caregiver-only page (hamburger menu destinations)
  const isCaregiverPage = isCaregiverRoute(location.pathname);

  // Update document direction and language when it changes
  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = isRTL ? "he" : "en";
  }, [dir, isRTL]);

  const handleSideMenuAction = (action: string) => {
    switch (action) {
      case "help":
        navigate("/help");
        break;
      case "rest":
        navigate("/rest");
        break;
      case "clock":
        navigate("/time");
        break;
      case "volume":
        navigate("/volume");
        break;
      case "back":
        if (onBack) {
          onBack();
        } else {
          navigate(-1);
        }
        break;
    }
  };

  // Expose sidebar actions to parent
  useEffect(() => {
    if (onSidebarActionsReady) {
      onSidebarActionsReady({
        onHelp: () => navigate("/help"),
        onRest: () => navigate("/rest"),
        onClock: () => navigate("/time"),
        onVolume: () => navigate("/volume"),
      });
    }
  }, [onSidebarActionsReady, navigate]);

  // Apply font size class only to user-facing screens, caregiver pages stay at default
  const fontSizeClass = isCaregiverPage ? "" : `text-size-${currentFontSize}`;

  return (
    <DeviceCheck>
      <div className={cn("app-container flex flex-col h-[100dvh] w-full bg-background overflow-hidden", fontSizeClass)} dir={dir} data-design-id="page-bg">
        <div className="flex flex-1 overflow-hidden">
          {!hideSidebar && (
            <Sidebar
              onAction={handleSideMenuAction}
              onBack={onBack}
            />
          )}

          {/* Main Content */}
          <main
            className={cn(
              "flex-1 flex flex-col overflow-hidden relative",
              "transition-all duration-200"
            )}
            data-scan-zone="main"
          >
            <div className="flex-1 flex flex-col overflow-hidden">
              {children}
            </div>
          </main>
        </div>

        {/* StatusBar at full width */}
        <StatusBar />
      </div>
    </DeviceCheck>
  );
}
